// feat: define badge definitions and unlock conditions

export interface BadgeDefinition {
  id: string;
  name: string;
  description: string;
  category: 'creator' | 'reader' | 'learner' | 'social' | 'streak';
  condition: {
    type: 'count' | 'streak' | 'milestone' | 'one-time';
    target: number;
    metric?: string;
  };
}

/**
 * Available badges in the system
 */
export const BADGES: BadgeDefinition[] = [
  // Creator badges
  {
    id: 'first_note',
    name: 'Primeros pasos',
    description: 'Crea tu primera nota',
    category: 'creator',
    condition: { type: 'count', target: 1, metric: 'notes_created' },
  },
  {
    id: 'note_novice',
    name: 'Notero novato',
    description: 'Crea 10 notas',
    category: 'creator',
    condition: { type: 'count', target: 10, metric: 'notes_created' },
  },
  {
    id: 'note_master',
    name: 'Maestro de notas',
    description: 'Crea 100 notas',
    category: 'creator',
    condition: { type: 'count', target: 100, metric: 'notes_created' },
  },
  {
    id: 'visual_thinker',
    name: 'Pensador visual',
    description: 'Crea tu primer canvas',
    category: 'creator',
    condition: { type: 'count', target: 1, metric: 'canvases_created' },
  },

  // Reader badges
  {
    id: 'first_page',
    name: 'Pasapáginas',
    description: 'Lee tu primera página',
    category: 'reader',
    condition: { type: 'count', target: 1, metric: 'pages_read' },
  },
  {
    id: 'book_worm',
    name: 'Ratón de biblioteca',
    description: 'Lee 100 páginas',
    category: 'reader',
    condition: { type: 'count', target: 100, metric: 'pages_read' },
  },
  {
    id: 'speed_reader',
    name: 'Lector veloz',
    description: 'Lee 1000 páginas',
    category: 'reader',
    condition: { type: 'count', target: 1000, metric: 'pages_read' },
  },
  {
    id: 'book_finished',
    name: 'Finalizador',
    description: 'Completa tu primer libro',
    category: 'reader',
    condition: { type: 'count', target: 1, metric: 'books_completed' },
  },

  // Learner badges
  {
    id: 'first_lesson',
    name: 'Estudiante',
    description: 'Completa tu primera lección',
    category: 'learner',
    condition: { type: 'count', target: 1, metric: 'lessons_completed' },
  },
  {
    id: 'course_graduate',
    name: 'Graduado',
    description: 'Completa tu primer curso',
    category: 'learner',
    condition: { type: 'count', target: 1, metric: 'courses_completed' },
  },
  {
    id: 'course_master',
    name: 'Maestro de cursos',
    description: 'Completa 5 cursos',
    category: 'learner',
    condition: { type: 'count', target: 5, metric: 'courses_completed' },
  },

  // Streak badges
  {
    id: 'streak_3',
    name: 'En racha',
    description: 'Mantén una racha de 3 días',
    category: 'streak',
    condition: { type: 'streak', target: 3 },
  },
  {
    id: 'streak_7',
    name: 'Guerrero semanal',
    description: '7 días de racha',
    category: 'streak',
    condition: { type: 'streak', target: 7 },
  },
  {
    id: 'streak_30',
    name: 'Maestro mensual',
    description: '30 días de racha',
    category: 'streak',
    condition: { type: 'streak', target: 30 },
  },
  {
    id: 'streak_100',
    name: 'Centurión',
    description: '100 días de racha',
    category: 'streak',
    condition: { type: 'streak', target: 100 },
  },

  // Social badges
  {
    id: 'sharer',
    name: 'Compartidor',
    description: 'Comparte tu primer elemento',
    category: 'social',
    condition: { type: 'one-time', target: 1 },
  },
  {
    id: 'organizer',
    name: 'Organizador',
    description: 'Usa tags en 10 elementos',
    category: 'social',
    condition: { type: 'count', target: 10, metric: 'tagged_items' },
  },
] as const;

/**
 * Get badge by ID
 */
export function getBadgeById(id: string): BadgeDefinition | undefined {
  return BADGES.find((b) => b.id === id);
}

/**
 * Get badges by category
 */
export function getBadgesByCategory(category: BadgeDefinition['category']): BadgeDefinition[] {
  return BADGES.filter((b) => b.category === category);
}
